import type { CSSProperties } from "react";

const keyframes = `@keyframes skeletonPulse{0%,100%{opacity:1}50%{opacity:0.5}}`;

const pulse: CSSProperties = {
  animation: "skeletonPulse 1.5s ease-in-out infinite",
};

// ── Skeleton-specific styles ──────────────────────────────────────

const sk: Record<string, CSSProperties> = {
  heroNumber: {
    width: 60,
    height: 46,
    borderRadius: 8,
    background: "rgba(255,255,255,0.08)",
  },
  heroUnit: {
    width: 32,
    height: 10,
    borderRadius: 4,
    background: "rgba(255,255,255,0.06)",
    marginTop: 4,
  },
  heroLine1: {
    width: 100,
    height: 10,
    borderRadius: 4,
    background: "rgba(255,255,255,0.1)",
    marginBottom: 6,
  },
  heroLine2: {
    width: 160,
    height: 12,
    borderRadius: 4,
    background: "rgba(255,255,255,0.06)",
  },
  statVal: {
    width: 28,
    height: 18,
    borderRadius: 4,
    background: "#F0F0F0",
    margin: "0 auto 4px",
  },
  statLabel: {
    width: 44,
    height: 10,
    borderRadius: 3,
    background: "#F5F5F5",
    margin: "0 auto",
  },
  weekCircle: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    background: "#F0F0F0",
  },
  weekLabel: {
    width: 22,
    height: 8,
    borderRadius: 3,
    background: "#F5F5F5",
  },
  headerPlaceholder: {
    width: 64,
    height: 11,
    borderRadius: 3,
    background: "#F0F0F0",
  },
  headerPlaceholderWide: {
    width: 80,
    height: 11,
    borderRadius: 3,
    background: "#F0F0F0",
  },
  card: {
    margin: "0 20px 10px",
    padding: 14,
    borderRadius: 10,
    background: "#F8F9FC",
    height: 72,
  },
  sectionTitle: {
    width: 70,
    height: 12,
    borderRadius: 3,
    background: "#F0F0F0",
    marginBottom: 10,
  },
  milestone: {
    width: 68,
    height: 68,
    borderRadius: 10,
    background: "#F7F7F7",
    flexShrink: 0,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    background: "#F0F0F0",
  },
  ctaBtn: {
    width: "100%",
    height: 50,
    borderRadius: 12,
    background: "#F0F0F0",
  },
};

// ── Layout styles needed from the main component ──────────────────
// Passed in as `layout` prop to keep the skeleton decoupled from the
// main styles object while still matching the exact same dimensions.

export type SkeletonLayout = Record<
  | "wrap"
  | "hero"
  | "heroGlow"
  | "heroNumberBlock"
  | "heroInfo"
  | "statsRow"
  | "statItem"
  | "statItemLast"
  | "content"
  | "weekStrip"
  | "weekStripHeader"
  | "weekDays"
  | "weekDay"
  | "milestonesSection"
  | "milestoneTrack"
  | "stickyFooter"
  | "fda",
  CSSProperties
>;

export default function StreakSkeleton({ layout }: { layout: SkeletonLayout }) {
  return (
    <div style={layout.wrap}>
      <style>{keyframes}</style>

      {/* Hero */}
      <div style={layout.hero}>
        <div style={layout.heroGlow} />
        <div
          style={{
            ...layout.heroNumberBlock,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div style={{ ...sk.heroNumber, ...pulse }} />
          <div style={{ ...sk.heroUnit, ...pulse }} />
        </div>
        <div style={layout.heroInfo}>
          <div style={{ ...sk.heroLine1, ...pulse }} />
          <div style={{ ...sk.heroLine2, ...pulse }} />
        </div>
      </div>

      {/* Stats row */}
      <div style={layout.statsRow}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={i < 2 ? layout.statItem : layout.statItemLast}>
            <div style={{ ...sk.statVal, ...pulse }} />
            <div style={{ ...sk.statLabel, ...pulse }} />
          </div>
        ))}
      </div>

      {/* Content */}
      <div style={layout.content}>
        <div style={layout.weekStrip}>
          <div style={layout.weekStripHeader}>
            <div style={{ ...sk.headerPlaceholder, ...pulse }} />
            <div style={{ ...sk.headerPlaceholderWide, ...pulse }} />
          </div>
          <div style={layout.weekDays}>
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} style={layout.weekDay}>
                <div style={{ ...sk.weekLabel, ...pulse }} />
                <div style={{ ...sk.weekCircle, ...pulse }} />
              </div>
            ))}
          </div>
        </div>

        <div style={{ ...sk.card, ...pulse }} />

        <div style={layout.milestonesSection}>
          <div style={{ ...sk.sectionTitle, ...pulse }} />
          <div style={layout.milestoneTrack}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} style={{ ...sk.milestone, ...pulse }} />
            ))}
          </div>
        </div>

        <div style={{ padding: "0 20px 12px" }}>
          <div style={{ ...sk.progressBar, ...pulse }} />
        </div>
      </div>

      {/* CTA */}
      <div style={layout.stickyFooter}>
        <div style={{ ...sk.ctaBtn, ...pulse }} />
        <div style={layout.fda}>
          *These statements have not been evaluated by the FDA. This product is
          not intended to diagnose, treat, cure, or prevent any disease.
        </div>
      </div>
    </div>
  );
}
